import { useSearchParams } from 'react-router'

import type { PricedQuoteCard } from '@/components/quotes/quoteCardTypes'
import { PricedFilterPopover } from './PricedFilterPopover'
import { matchesPricedFilters, type PricedFilter, type PricedFilterSelection } from './pricedFilters'

export interface PricedItem {
  id: string
  quote: PricedQuoteCard
  description?: React.ReactNode
  content: React.ReactNode
}

interface PricedFilterViewProps {
  items: PricedItem[]
  filters: PricedFilter[]
}

/** Priced cards narrowed by a filter row like the quotes page's own. */
export function PricedFilterView({ items, filters }: PricedFilterViewProps) {
  // In the URL, e.g. ?card=indications&status=reviewing&price=carrier, so a link opens on the same cards.
  const [searchParams, setSearchParams] = useSearchParams()
  const selection: PricedFilterSelection = Object.fromEntries(
    filters.map((filter) => [filter.key, new Set(searchParams.get(filter.key)?.split(',').filter(Boolean))])
  )

  const write = (filter: PricedFilter, ticked: ReadonlySet<string>) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        // Option order, not tick order, so the same selection always makes the same link.
        const values = filter.options
          .filter((option) => ticked.has(option.value))
          .map((option) => option.value)
        if (values.length) next.set(filter.key, values.join(','))
        else next.delete(filter.key)
        return next
      },
      { replace: true }
    )

  const toggle = (filter: PricedFilter, value: string) => {
    const next = new Set(selection[filter.key])
    if (next.has(value)) next.delete(value)
    else next.add(value)
    write(filter, next)
  }

  const matches = items.filter((item) => matchesPricedFilters(item.quote, filters, selection))

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center gap-3">
        {filters.map((filter) => (
          <PricedFilterPopover
            key={filter.key}
            filter={filter}
            ticked={selection[filter.key] ?? new Set()}
            onToggle={(value) => toggle(filter, value)}
            onClear={() => write(filter, new Set())}
          />
        ))}
      </div>

      {matches.length ? (
        matches.map((item) => (
          <div key={item.id} className="flex flex-col gap-6">
            {item.description}
            {item.content}
          </div>
        ))
      ) : (
        <p className="border-ink-200 text-ink-600 rounded-lg border border-dashed p-3 text-xs leading-relaxed">
          This state has not been mocked, or is not currently possible in production. Ask Claude to check.
        </p>
      )}
    </div>
  )
}
